import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { ChangeEvent, useEffect, useState } from 'react';
import { z } from 'zod';
import { useTranslation } from 'react-i18next';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Button, Card, InputLabel, Paper, Switch, Table, TableCell, TableContainer, TableHead, TableRow, TextField, Typography } from '@mui/material';
import Grid from '@mui/material/Unstable_Grid2';
import Upload from '../../common/Upload.tsx';
import Autocomplete from '@mui/material/Autocomplete';
import BasicModal from '../../common/BasicModal.tsx';
import { LoadingButton } from '@mui/lab';
import { CKEditor } from '@ckeditor/ckeditor5-react';
import ClassicEditor from '@ckeditor/ckeditor5-build-classic';
import { DndContext, closestCenter, useSensor, useSensors, KeyboardSensor, MouseSensor } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy, arrayMove } from '@dnd-kit/sortable';
import BlockItem from './BlockItem.tsx';

interface ITemplates_type {
	template: string;
	ref_type: 'image' | 'form' | 'content' | null;
}

const templates_studio_type = [
	{ template: 'slider', ref_type: 'image' },
	{ template: 'image_with_link', ref_type: 'image' },
	{ template: 'swiper_studio', ref_type: 'content' },
	{ template: 'post_studio', ref_type: 'content' },
	{ template: 'post_right_studio', ref_type: 'content' },
];

const templates_type = [
	{ template: 'contact_us', ref_type: 'image' },
	{ template: 'contact_ways', ref_type: 'image' },
	{ template: 'map', ref_type: 'image' },
	{ template: 'contact_form', ref_type: 'form' },
	{ template: 'contact_social_media', ref_type: 'image' },
	{ template: 'footer_list', ref_type: null }, //
	{ template: 'footer_list_icon', ref_type: null }, //
	{ template: 'homepage_top', ref_type: 'image' }, //
	{ template: 'stats', ref_type: 'image' }, //
	{ template: 'baran_media', ref_type: 'image' }, //
	{ template: 'swiper', ref_type: 'image' }, //
	{ template: 'why_us', ref_type: 'image' }, //
	{ template: 'about_us', ref_type: 'image' },
	{ template: 'about_us_stat', ref_type: 'image' },
	{ template: 'about_us_info', ref_type: 'image' },
	{ template: 'about_baran_team', ref_type: 'content' },
	{ template: 'footer_list', ref_type: null },
	{ template: 'slider', ref_type: 'image' },
	{ template: 'swiper_studio', ref_type: 'content' },
	{ template: 'image_with_link', ref_type: 'image' },
	{ template: 'post_studio', ref_type: 'content' },
	{ template: 'post_right_studio', ref_type: 'content' },
	{ template: 'need_advice', ref_type: null },
	// {template: 'content_list_items', ref_type: ''},
	// {template: 'content_left_image', ref_type: ''},
	// {template: 'content_right_image', ref_type: ''},
];

const sectionType = ['header', 'main', 'footer'];

const configForm = z.object({
	title: z.string().min(3, 'invalid_min_title_message_block'),
	description: z.any(),
	link: z.string().url(),
	image: z.any(),
});

interface IItem {
	id: number;
	title: string;
	description: string;
	link: string;

	image_id?: any;
	mobile_image_id?: any;

	image: string | File;
	imageMobile: string | File;
}

export default function Manage({ isStodio }: { isStodio?: true }) {
	const mouseSensor = useSensor(MouseSensor, {
		activationConstraint: {
			distance: 5,
		},
	});
	const keyboardSensor = useSensor(KeyboardSensor);
	const sensors = useSensors(mouseSensor, keyboardSensor);

	const { t } = useTranslation();

	const navigate = useNavigate();

	const { id } = useParams();

	const [status, setStatus] = useState(false);
	const [showTitle, setShowTitle] = useState(false);

	const [loadingButton, setLoadingButton] = useState(false);
	const [section, setSection] = useState('');
	const [image, setImage] = useState<(string | File)[]>([]);
	const [showItemDialog, setShowItemDialog] = useState<true | number | false>(false);
	const [template, setTemplate] = useState<ITemplates_type>();

	const [prevIMageId, setPrevImageId] = useState();

	const [titleDialogError, setTitleDialogError] = useState('');

	const [items, setItems] = useState<IItem[]>([]);

	const [titleItem, setTitleItem] = useState('');
	const [linkItem, setLinkItem] = useState('');
	const [descriptionItem, setDescriptionItem] = useState('');
	const [imageItem, setImageItem] = useState<(string | File)[]>([]);
	const [imageMobileItem, setImageMobileItem] = useState<(string | File)[]>([]);

	const [lockModal, setLockModal] = useState(false);

	const {
		register,
		handleSubmit,
		formState: { errors },
		control,
		setValue,
	} = useForm<z.infer<typeof configForm>>({
		resolver: zodResolver(configForm),
	});

	useEffect(() => {
		if (!id) {
			if (isStodio) {
				setSection('studio');
			} else {
				setSection(sectionType?.[1] ?? '');
			}
			return;
		}

		axios({
			url: `/admin/block/${id}`,
			method: 'GET',
		}).then(r => {
			if (r?.data?.success) {
				const data = r?.data?.data;
				setSection(isStodio ? 'studio' : data?.section);
				setTemplate(templates_type?.find(t => (t?.template ?? '') === (data?.template ?? '')) as ITemplates_type | undefined);
				setStatus(!!data?.status);
				setShowTitle(!!data?.show_title);
				setValue('title', data?.title);

				setPrevImageId(data?.image_id);

				setValue('description', data?.description);
				setValue('link', data?.link);
				setImage(data?.image ? [data?.image_address] : []);
				setItems(data?.data?.map((d: any, index: number) => ({ ...d, id: index + 1, imageMobile: d?.mobile_image })));
			}
		});
	}, [id, isStodio]);
	const handleEditItem = (id: number) => {
		setTitleItem(items?.[id - 1]?.title ?? '');
		setLinkItem(items?.[id - 1]?.link ?? '');
		setDescriptionItem(items?.[id - 1]?.description ?? '');
		setImageItem(items?.[id - 1]?.image ? [items?.[id - 1]?.image] : []);
		setImageMobileItem(items?.[id - 1]?.imageMobile ? [items?.[id - 1]?.imageMobile] : []);
		setShowItemDialog(id);
	};

	const handleDeleteItem = (id: number) => {
		setItems(p => [...p?.slice(0, id - 1), ...p?.slice(id)?.map(i => ({ ...i, id: i?.id - 1 }))]);
	};

	const resetDataItem = () => {
		setTitleItem('');
		setLinkItem('');
		setDescriptionItem('');
		setImageItem([]);
		setImageMobileItem([]);
	};

	const openItemDialog = () => {
		resetDataItem();
		setShowItemDialog(true);
	};

	const closeItemDialog = () => setShowItemDialog(false);

	const handleChangeTemplate = (_: any, value: string | ITemplates_type | null) => {
		if (value && typeof value !== 'string') setTemplate(value);
	};

	const handleChangeSection = (e: any, value: any) => {
		if (e?.target?.value === undefined) return;

		if (e?.target?.value === 0) {
			setSection(value ?? '');
		} else {
			setSection(e?.target?.value ?? '');
		}
	};

	const handleChangeTitleItem = (e: ChangeEvent<HTMLInputElement>) => {
		setTitleItem(e?.target?.value ?? '');
	};

	const handleChangeLinkItem = (e: ChangeEvent<HTMLInputElement>) => {
		setLinkItem(e?.target?.value ?? '');
	};

	const handleChangeDescriptionItem = (e: ChangeEvent<HTMLInputElement>) => {
		setDescriptionItem(e?.target?.value ?? '');
	};

	const handleSubmitItemDialog = () => {
		if (titleItem) {
			setTitleDialogError('');
		} else {
			setTitleDialogError(t('title_required'));
			return;
		}

		if (lockModal) return;

		setLockModal(true);

		if (typeof showItemDialog === 'number') {
			// edit
			setItems(p => [
				...p?.slice(0, showItemDialog - 1),
				{
					id: showItemDialog,
					title: titleItem,
					link: linkItem,
					description: descriptionItem,
					image: imageItem?.[0],
					imageMobile: imageMobileItem?.[0],

					image_id: !!imageItem?.[0] && typeof imageItem?.[0] !== 'object' ? p?.[showItemDialog - 1]?.image_id : undefined,
					mobile_image_id: !!imageMobileItem?.[0] && typeof imageMobileItem?.[0] !== 'object' ? p?.[showItemDialog - 1]?.mobile_image_id : undefined,
				},
				...p?.slice(showItemDialog),
			]);
		} else {
			// create
			setItems(p => [
				...p,
				{
					id: (p?.length ?? 0) + 1,
					title: titleItem,
					link: linkItem,
					description: descriptionItem,
					image: imageItem?.[0],
					imageMobile: imageMobileItem?.[0],
				},
			]);
		}

		resetDataItem();
		closeItemDialog();

		setLockModal(false);
	};

	const handleSaveBlock = (data: any) => {
		setLoadingButton(true);
		const newData = new FormData();

		newData.append(`show_title`, showTitle ? '1' : '0');
		newData.append(`title`, data?.title ?? '');
		newData.append(`description`, data?.description ?? '');
		newData.append(`status`, status ? '1' : '0');
		newData.append(`link`, data?.link ?? '');

		if (typeof image?.[0] === 'object') {
			newData.append(`image_block`, image?.[0] ?? '');
		} else {
			newData.append(`image_block`, prevIMageId?.[0] ?? '');
		}

		items.forEach((i, index) => {
			newData.append(`block_items[${index}][title]`, i?.title ?? '');
			newData.append(`block_items[${index}][link]`, i?.link ?? '');
			newData.append(`block_items[${index}][description]`, i?.description ?? '');

			if (i?.image_id) {
				newData.append(`block_items[${index}][image]`, i?.image_id ?? '');
			} else {
				newData.append(`block_items[${index}][image]`, i?.image ?? '');
			}

			if (i?.mobile_image_id) {
				newData.append(`block_items[${index}][imageMobile]`, i?.mobile_image_id ?? '');
			} else {
				newData.append(`block_items[${index}][imageMobile]`, i?.imageMobile ?? '');
			}
		});

		newData.append(`ref_type`, template?.ref_type ?? '');
		newData.append(`template`, template?.template ?? '');
		newData.append(`section`, section);

		if (id) {
			newData.append('_method', 'put');
		}

		axios({
			url: `/admin/block${id ? `/${id}` : ''}`,
			method: `POST`,
			data: newData,
		})
			.then(r => {
				if (r?.data?.success) {
					navigate('/block/list');
				}
			})
			.finally(() => {
				setLoadingButton(false);
			});
	};

	const handleChangeStatus = (event: ChangeEvent<HTMLInputElement>) => {
		setStatus(event.target.checked);
	};

	const handleChangeShowTitle = (event: ChangeEvent<HTMLInputElement>) => {
		setShowTitle(event.target.checked);
	};

	const handleCancelBlock = () => {
		navigate(-1);
	};

	const handleDragEnd = (event: any) => {
		const { active, over } = event;
		if (!active.id !== over.id) {
			setItems(items => {
				const oldIndex = items.findIndex(person => person.id === active.id);
				const newIndex = items.findIndex(person => person.id === over.id);
				return arrayMove(items, oldIndex, newIndex);
			});
		}
	};

	return (
		<Card className={'w-full px-6 py-12'}>
			<form onSubmit={handleSubmit(handleSaveBlock)}>
				<Grid container spacing={4}>
					<Grid md={6}>
						<div className={'flex flex-col gap-6'}>
							<div>
								<InputLabel className="float-start flex px-2 pb-2">{t('title')}</InputLabel>
								<TextField variant="outlined" fullWidth size="small" {...register('title')} error={!!errors.title} helperText={t(errors.title?.message ?? '')} />
							</div>

							<div className={'flex items-center'}>
								<InputLabel className="float-start flex px-2">{t('status')}</InputLabel>
								<Switch size="small" checked={status} onChange={handleChangeStatus} />
							</div>

							<div>
								<InputLabel className="float-start flex px-2 pb-2">{t('type_items')}</InputLabel>
								<Autocomplete
									key={template?.template || 'default-key'}
									size={'small'}
									value={template}
									getOptionLabel={(option: string | ITemplates_type) => {
										if (typeof option === 'string') {
											return option;
										}

										return `${t(option.ref_type ?? 'with_out_type')} (${option.template})`;
									}}
									onChange={handleChangeTemplate}
									renderInput={params => <TextField {...params} />}
									options={isStodio ? (templates_studio_type as any) : (templates_type as any)}
									renderOption={(props, option: any) => {
										return (
											<li {...props}>
												<div className={'flex gap-2'}>
													<Typography>{t(option?.ref_type ?? 'with_out_type')}</Typography>
													<Typography className={'text-sm'}>({option.template})</Typography>
												</div>
											</li>
										);
									}}
									className={'w-full'}
								/>
							</div>

							<div>
								<InputLabel className="float-start flex px-2">{t('description')}</InputLabel>
								<div className={'mt-8'}>
									<Controller
										name="description"
										control={control}
										render={({ field }) => (
											<CKEditor
												editor={ClassicEditor}
												config={{
													ckfinder: {
														uploadUrl: import.meta.env.VITE_BASE_URL + '/upload-file',
													},
												}}
												onReady={(editor: any) => {
													console.log('Editor is ready to use!', editor);
												}}
												data={field.value || ''}
												onChange={(editor: any) => {
													const data = editor.getData();
													field.onChange(data);
												}}
											/>
										)}
									/>
								</div>
							</div>
						</div>
					</Grid>

					<Grid md={6}>
						<div className={'flex flex-col gap-6'}>
							<div>
								<InputLabel className="float-start flex px-2 pb-2">{t('section')}</InputLabel>
								<Autocomplete freeSolo disabled={isStodio} size={'small'} value={section} onInputChange={handleChangeSection} renderInput={params => <TextField {...params} />} options={sectionType} className={'w-full'} />
							</div>

							<div className={'flex items-center'}>
								<InputLabel className="float-start flex px-2">{t('show_title')}</InputLabel>
								<Switch size="small" checked={showTitle} onChange={handleChangeShowTitle} />
							</div>

							<div>
								<InputLabel className="float-start flex px-2 pb-2">{t('link')}</InputLabel>
								<TextField variant="outlined" fullWidth size="small" {...register('link')} error={!!errors.link} helperText={t(errors.link?.message ?? '')} />
							</div>

							<div>
								<InputLabel className="float-start flex px-2 pb-2">{t('image')}</InputLabel>
								<Upload text={t('upload_image')} setFiles={setImage} files={image} className={'flex w-full'} />
							</div>
						</div>
					</Grid>
				</Grid>
				{[null, 'content', 'image']?.includes(template?.ref_type !== undefined ? template?.ref_type : '') && (
					<div className={'mt-8'}>
						<Button variant={'contained'} onClick={openItemDialog} className={'mb-4'}>
							{t('add_item')}
						</Button>

						<DndContext collisionDetection={closestCenter} onDragEnd={handleDragEnd} sensors={sensors}>
							<SortableContext items={items} strategy={verticalListSortingStrategy}>
								<TableContainer component={Paper}>
									{' '}
									<Table sx={{ minWidth: 650 }} aria-label="simple table">
										{' '}
										<TableHead>
											{' '}
											<TableRow>
												<TableCell align="center">{t('id')}</TableCell>
												<TableCell align="center">{t('title')}</TableCell>
												<TableCell align="center">{t('link')}</TableCell>
												<TableCell align="center">{t('image')}</TableCell>
												<TableCell align="center">{t('mobile_image')}</TableCell> <TableCell align="left">{t('action')}</TableCell>{' '}
											</TableRow>{' '}
										</TableHead>{' '}
										{items.map(item => (
											<BlockItem item={item} handleEditItem={handleEditItem} handleDeleteItem={handleDeleteItem} />
										))}
									</Table>{' '}
								</TableContainer>
							</SortableContext>
						</DndContext>
					</div>
				)}

				<div className={'flex gap-2'}>
					<LoadingButton loading={loadingButton} variant={'contained'} className={'mt-6 px-6'} size={'large'} type="submit">
						{t('submit')}
					</LoadingButton>

					<Button variant={'outlined'} className={'mt-6 opacity-30'} color={'inherit'} onClick={handleCancelBlock}>
						{t('cancel')}
					</Button>
				</div>
			</form>

			<BasicModal
				open={!!showItemDialog}
				onClose={closeItemDialog}
				title={typeof showItemDialog === 'number' ? t('edit_item') : t('add_item')}
				titleClassName={'text-center grow '}
				subClassName={'flex flex-row-reverse'}
				className={'max-w-sm rounded-lg'}
				showBackIcon={false}
				action={
					<>
						<Button onClick={closeItemDialog} color="inherit">
							{t('cancel')}
						</Button>
						<LoadingButton onClick={handleSubmitItemDialog} variant="outlined" color="error">
							{t('submit')}
						</LoadingButton>
					</>
				}
			>
				<div className={'flex flex-col gap-4 pb-4'}>
					<div>
						<InputLabel className="float-start flex px-2 pb-2">{t('title')}</InputLabel>
						<TextField variant="outlined" fullWidth size="small" value={titleItem} onChange={handleChangeTitleItem} error={!!titleDialogError} helperText={titleDialogError} />
					</div>
					<div>
						<InputLabel className="float-start flex px-2 pb-2">{t('link')}</InputLabel>
						<TextField variant="outlined" fullWidth size="small" value={linkItem} onChange={handleChangeLinkItem} />
					</div>
					<div>
						<InputLabel className="float-start flex px-2 pb-2">{t('description')}</InputLabel>
						<TextField variant="outlined" fullWidth size="small" value={descriptionItem} onChange={handleChangeDescriptionItem} multiline rows={5} />
					</div>
					<div>
						<InputLabel className="float-start flex px-2 pb-2">{t('image')}</InputLabel>
						<Upload text={t('upload_image')} setFiles={setImageItem} files={imageItem} className={'flex w-full'} />
					</div>
					<div>
						<InputLabel className="float-start flex px-2 pb-2">{t('mobile_image')}</InputLabel>
						<Upload text={t('upload_image')} setFiles={setImageMobileItem} files={imageMobileItem} className={'flex w-full'} />
					</div>
				</div>
			</BasicModal>
		</Card>
	);
}
